import { Injectable } from '@angular/core';
import { StyleThemeAdapter } from '@b2b/features/misc/style-theme/core/style-theme.adapter';
import { StyleTheme } from '@b2b/features/misc/style-theme/core/style-theme.model';
import { take } from 'rxjs';
import { ToasterService } from '@shared/components/toaster/toaster.service';
import { TOASTER_TYPE } from '@shared/components/toaster/toaster.model';

@Injectable({
  providedIn: 'root',
})
export class StyleThemeService {
  constructor(
    private styleThemeAdapter: StyleThemeAdapter,
    private toasterService: ToasterService,
  ) {}

  load() {
    this.styleThemeAdapter
      .getTheme()
      .pipe(take(1))
      .subscribe(request => {
        if (request.hasError) {
          this.toasterService.init({
            type: TOASTER_TYPE.DANGER,
            content: request.message || '',
          });
        } else {
          const css = this.convertToCSS(request.theme!);
          this.injectStyle(css, request.theme!);
        }
      });
  }

  private convertToCSS(style: StyleTheme): string {
    const c = style.theme?.colors;
    const core = style.core;

    // Gera versão semi-transparente (25% opacidade) da cor primária para sombras de foco
    const primaryAlpha25 = c?.primary ? `${c.primary}40` : 'rgba(200, 29, 23, 0.25)';

    // Gera a escala de tints/shades da cor primária (50–950)
    const primaryScale = c?.primary ? this.generateColorScale(c.primary) : null;
    const primaryScaleCSS = primaryScale
      ? Object.entries(primaryScale)
          .map(([key, val]) => `        --cx-color-primary-${key}: ${val};`)
          .join('\n')
      : '';

    return `
      :root {
        /* === Cores de texto e layout === */
        --cx-text-common-color                      : ${core?.common?.colors?.text};
        --cx-link-common-color                      : ${core?.common?.colors?.link};
        --cx-page-common-background-color           : ${core?.common?.colors?.background};

        /* === Card === */
        --cx-card-background-color                  : ${core?.simpleCard?.colors?.background};
        --cx-card-background-transparent-color      : ${core?.simpleCard?.colors?.backgroundTransparent};

        /* === Cores principais === */
        --cx-color-primary                          : ${c?.primary};
        --cx-color-secondary                        : ${c?.secondary};
        --cx-color-success                          : ${c?.success};
        --cx-color-danger                           : ${c?.danger};
        --cx-color-warning                          : ${c?.warning};
        --cx-color-info                             : ${c?.info};

        /* === Alpha 25% (para sombras de foco) === */
        --cx-color-primary-alpha-25                 : ${primaryAlpha25};

        /* === Escala de cor primária (50–950) === */
${primaryScaleCSS}

        /* === Variações light (-10% brilho) === */
        --cx-color-primary-light                    : ${c?.light?.primary};
        --cx-color-secondary-light                  : ${c?.light?.secondary};
        --cx-color-success-light                    : ${c?.light?.success};
        --cx-color-danger-light                     : ${c?.light?.danger};
        --cx-color-warning-light                    : ${c?.light?.warning};
        --cx-color-info-light                       : ${c?.light?.info};

        /* === Variações dark (+10% brilho) === */
        --cx-color-primary-dark                     : ${c?.dark?.primary};
        --cx-color-secondary-dark                   : ${c?.dark?.secondary};
        --cx-color-success-dark                     : ${c?.dark?.success};
        --cx-color-danger-dark                      : ${c?.dark?.danger};
        --cx-color-warning-dark                     : ${c?.dark?.warning};
        --cx-color-info-dark                        : ${c?.dark?.info};
      }
    `;
  }

  /**
   * Gera uma escala de tints (50–500) e shades (600–950) a partir de uma cor primária em hex.
   * - 50–500: interpolação do primary em direção ao branco
   * - 600: a cor primária pura
   * - 700–950: interpolação do primary em direção ao preto
   */
  private generateColorScale(hex: string): Record<string, string> {
    const parse = (h: string) => ({
      r: parseInt(h.replace('#', '').slice(0, 2), 16),
      g: parseInt(h.replace('#', '').slice(2, 4), 16),
      b: parseInt(h.replace('#', '').slice(4, 6), 16),
    });

    const toHex = (r: number, g: number, b: number): string => {
      const clamp = (v: number) => Math.min(255, Math.max(0, Math.round(v)));
      return `#${clamp(r).toString(16).padStart(2, '0')}${clamp(g).toString(16).padStart(2, '0')}${clamp(b).toString(16).padStart(2, '0')}`;
    };

    /** Interpola `weight`% da primary em direção ao target (0 = primary, 1 = target) */
    const mix = (
      p: { r: number; g: number; b: number },
      target: { r: number; g: number; b: number },
      weight: number,
    ): string =>
      toHex(p.r + (target.r - p.r) * weight, p.g + (target.g - p.g) * weight, p.b + (target.b - p.b) * weight);

    const primary = parse(hex);
    const white = { r: 255, g: 255, b: 255 };
    const black = { r: 0, g: 0, b: 0 };

    return {
      '50': mix(primary, white, 0.95),
      '100': mix(primary, white, 0.88),
      '200': mix(primary, white, 0.76),
      '300': mix(primary, white, 0.62),
      '400': mix(primary, white, 0.46),
      '500': mix(primary, white, 0.28),
      '600': hex,
      '700': mix(primary, black, 0.18),
      '800': mix(primary, black, 0.35),
      '900': mix(primary, black, 0.52),
      '950': mix(primary, black, 0.65),
    };
  }

  private injectStyle(css: string, style: StyleTheme): void {
    // Remove injeção anterior para evitar duplicação
    const existing = document.querySelector('style[data-theme]');
    if (existing) {
      existing.remove();
    }

    const $style = document.createElement('style');
    $style.setAttribute('data-theme', style.name || '');
    $style.innerHTML = css;
    document.head.appendChild($style);
  }
}
